import React from "react";
import { Tag } from "primereact/tag";
import { Avatar } from "primereact/avatar";
import moment from "moment";

const PriorityTag = ({ value }) => {
  const map = {
    Critical: "tag-critical",
    High: "tag-high",
    Medium: "tag-medium",
    Low: "tag-low",
  };
  return <Tag value={value} className={`p-tag ${map[value] || ""}`} />;
};

const StatusTag = ({ value }) => {
  const map = {
    "In Progress": "tag-progress",
    Open: "tag-open",
    Resolved: "tag-resolved",
    Closed: "tag-closed",
    New: "tag-new",
    Pending: "tag-pending",
    "On Hold": "tag-onhold",
  };
  return <Tag value={value} className={`p-tag ${map[value] || ""}`} />;
};

const StatCard = ({
  num,
  label,
  delta,
  deltaUp,
  colorClass,
  onClick,
  style,
}) => (
  <div
    className={`stat-card ${colorClass}`}
    onClick={onClick}
    style={{ ...style, cursor: onClick ? "pointer" : undefined }}
  >
    <div className="stat-num">{num}</div>
    <div className="stat-lbl">{label}</div>
    {delta && (
      <div className={`stat-delta ${deltaUp ? "delta-up" : "delta-down"}`}>
        {delta}
      </div>
    )}
  </div>
);

const EssCard = ({ title, icon, children, style }) => (
  <div className="ess-card" style={style}>
    {title && (
      <div className="ess-card-title">
        <i className={`pi ${icon}`} />
        {title}
      </div>
    )}
    {children}
  </div>
);

const MiniBarChart = ({ title, rows = [], departments = [] }) => (
  <div className="mini-chart">
    <div className="mini-chart-title">{title}</div>
    <div style={{ maxHeight: 200, overflowY: "auto" }}>
      {(Array.isArray(rows) ? rows : []).map((r, i) => (
        <div className="bar-row" key={i}>
          <div className="bar-label" title={r.label}>{r.label}</div>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{ width: r.pct + "%", background: r.color }}
            />
          </div>
          <div
            className="bar-val"
            style={{ color: r.color === "#e53935" ? r.color : undefined }}
            title={r.deptCounts && departments.length
              ? Object.entries(r.deptCounts).map(([dId, cnt]) => {
                  const dept = departments.find(d => String(d.value) === String(dId));
                  return `${dept?.label ?? dId}: ${cnt}`;
                }).join(' | ')
              : undefined
            }
          >
            {r.val}
            {r.deptCounts && departments.length > 1 && (
              <span style={{ fontSize: 9, color: 'var(--muted)', marginLeft: 3 }}>ⓘ</span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CommentThread = ({ comments = [], isPrivileged = false }) => (
  <div>
    {(Array.isArray(comments) ? comments : []).map((c, i) => {
      if (c.isInternalNote && !isPrivileged) return null;

      const actualName = c.author
        ? `${c.author.firstName ?? ""} ${c.author.lastName ?? ""}`.trim()
        : (c.name ?? "—");
      const dn = c.author?.agentProfile?.displayName;
      const displayName = Array.isArray(dn) ? dn[0] : dn;
      const shownName = displayName || actualName;
      const avatar = shownName ? shownName.substring(0, 2).toUpperCase() : "?";
      const text = c.message ?? c.text ?? "";
      const timeRaw = c.createdAt ?? c.time;
      const formattedTime = timeRaw ? moment(timeRaw).format("DD MMM YYYY, hh:mm A") : "";

      // Approval-request comments — show as a distinct card
      if (c.isApprovalRequest) {
        const statusColor = c.approvalStatus === "approved" ? "#22a06b" : c.approvalStatus === "rejected" ? "#e53935" : "#b45309";
        const statusLabel = c.approvalStatus === "approved" ? "✔ Approved" : c.approvalStatus === "rejected" ? "✘ Rejected" : "⏳ Pending";
        return (
          <div key={i} style={{ background: "#fffbeb", border: "1.5px solid #f59e0b", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <i className="pi pi-shield" style={{ color: "#b45309", fontSize: 14 }} />
              <span style={{ fontWeight: 700, fontSize: 12, color: "#92400e" }}>Approval Request</span>
              <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: statusColor }}>{statusLabel}</span>
            </div>
            <div style={{ fontSize: 12.5, color: "#78350f", marginBottom: 4 }}>{text}</div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>{shownName} · {formattedTime}</div>
            {c.approvalComment && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#1a2a3a", fontStyle: "italic" }}>Response: {c.approvalComment}</div>
            )}
          </div>
        );
      }

      return (
        <div
          className="comment-wrap"
          key={i}
          style={c.isInternalNote ? { background: "#fffbeb", border: "1px dashed #f59e0b", borderRadius: 8, padding: "6px 8px" } : undefined}
        >
          <Avatar
            label={avatar}
            shape="circle"
            size="normal"
            style={{
              background: c.isInternalNote ? "#f59e0b" : "#2563a8",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              flexShrink: 0,
            }}
          />

          <div className="comment-body">
            <div className="comment-meta">
              <span className="comment-name">
                {shownName}
                {isPrivileged && displayName && displayName !== actualName && (
                  <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 400, marginLeft: 5 }}>
                    ({actualName})
                  </span>
                )}
              </span>
              {c.isInternalNote && (
                <span style={{ fontSize: 10, fontWeight: 700, color: "#b45309", background: "#fef3c7", borderRadius: 4, padding: "1px 6px", marginLeft: 6 }}>
                  🔒 Internal Note
                </span>
              )}
              <span className="comment-time">{formattedTime}</span>
            </div>

            <div className="comment-text">{text}</div>
          </div>
        </div>
      );
    })}
  </div>
);
export {
  PriorityTag,
  StatusTag,
  StatCard,
  EssCard,
  MiniBarChart,
  CommentThread,
};
